import { withDevtools } from "@angular-architects/ngrx-toolkit";
import { HttpClient } from "@angular/common/http";
import { effect, inject, untracked } from "@angular/core";
import { getState, patchState, signalStore, withHooks, withMethods, withState } from "@ngrx/signals";
import { rxMethod } from "@ngrx/signals/rxjs-interop";
import { catchError, combineLatest, EMPTY, map, pipe, switchMap, tap } from "rxjs";
import { ApiService } from "../../api/api.service";
import { UserSettings } from "../../api/interfaces";
import { AuthService } from "../../auth/auth.service";
import { ThemeMode } from "../../services/theme.service";

export interface LocaleOption {
    value: string;
    label: string;
    optionalLabel: string;
}

type PossibleLocales = Record<string, [string, ...unknown[]]>;

export interface SettingsState {
    themeMode: ThemeMode;
    localeKeyOptions: LocaleOption[];
    languageLocale: string;

    numberLocale: string;
    currency: string;
    dateFormat: string;
    timeFormat: string;
    isIsoWeek: boolean;

    v2TableColumnFilter: string[];
    v2TenantPanelFilter: string[];
    v2TenantPanelFilterExpanded: { id: string, expanded: boolean }[];

    v3TableColumnFilter: string[];
    v3TenantPanelFilter: string[];
    v3TenantPanelFilterExpanded: { id: string, expanded: boolean }[];

    locatorTableColumnFilter: string[];
    locatorTenantPanelFilter: string[];
    locatorTenantPanelFilterExpanded: { id: string, expanded: boolean }[];

    userManagementTableColumnFilter: string[];
    contactsTableColumnFilter: string[];
    v2ContactsTableColumnFilter: string[];
    v3ContactsTableColumnFilter: string[];
    syncContactTables: boolean;

    settingsLoading: boolean;
    debouncedSettingsLoading: boolean;

    error: any;
}

const initialState: SettingsState = {
    themeMode: 'system',
    localeKeyOptions: [],
    languageLocale: 'en_US',

    numberLocale: "de_AT",
    currency: "USD",
    dateFormat: "MM/DD/yyyy",
    timeFormat: "hh:mm A",
    isIsoWeek: true,

    v2TableColumnFilter: [],
    v2TenantPanelFilter: [],
    v2TenantPanelFilterExpanded: [],

    v3TableColumnFilter: [],
    v3TenantPanelFilter: [],
    v3TenantPanelFilterExpanded: [],

    locatorTableColumnFilter: [],
    locatorTenantPanelFilter: [],
    locatorTenantPanelFilterExpanded: [],

    userManagementTableColumnFilter: [],
    contactsTableColumnFilter: [],
    v2ContactsTableColumnFilter: [],
    v3ContactsTableColumnFilter: [],
    syncContactTables: false,

    settingsLoading: false,
    debouncedSettingsLoading: false,
    error: null
}


export const SettingsStore = signalStore(
    { providedIn: 'root' },
    withDevtools('SettingsStore'),
    withState(initialState),
    withMethods((store, auth = inject(AuthService), http = inject(HttpClient), api = inject(ApiService)) => {
        const methods = {

            inizializeSettingsStore: () => {
                patchState(store, {
                    settingsLoading: true,
                    error: null
                });
                methods.loadLocaleKeyOptions();
            },

            loadLocaleKeyOptions: rxMethod<void>(
                pipe(
                    tap(() =>
                        patchState(store, {
                            settingsLoading: true,
                            error: null
                        })),
                    switchMap(() =>
                        combineLatest([
                            http.get<string[]>('/i18n/locale-keys.json', {
                                // responseType: 'json',
                                headers: { 'Cache-Control': 'no-cache' },
                            }),
                            http.get<PossibleLocales>('/i18n/global-locales.json', {
                                // responseType: 'json',
                                headers: { 'Cache-Control': 'no-cache' },
                            }),
                        ]).pipe(
                            map(([locales, possibleLocales]) => {
                                const sanitizedGlobalLocales = Object.keys(possibleLocales).map((key) => ({
                                    value: key,
                                    label: possibleLocales[key][0],
                                    optionalLabel: possibleLocales[key][0],
                                }));

                                return locales
                                    .map((localeKey) =>
                                        sanitizedGlobalLocales.find((item) => item.value === localeKey)
                                    )
                                    .filter((item): item is LocaleOption => !!item);
                            }),
                            tap((localeKeys) => {
                                patchState(store, {
                                    localeKeyOptions: localeKeys,
                                    settingsLoading: false,
                                });
                            }),
                            catchError((error) => {
                                patchState(store, {
                                    settingsLoading: false,
                                    error: error,
                                });
                                return EMPTY;
                            })
                        )
                    )
                )
            ),

            saveUserSettings: rxMethod<Partial<UserSettings>>(
                pipe(
                    tap(() => {
                        patchState(store, {
                            error: null,
                            settingsLoading: true,
                        });
                    }),

                    switchMap((partialUpdate) => {
                        const {
                            settingsLoading,
                            error,
                            localeKeyOptions,
                            ...currentSettings
                        } = getState(store);

                        const fullSettings: UserSettings = {
                            ...currentSettings,
                            ...partialUpdate,
                        };


                        return api.updateCurrentUserSettings(fullSettings).pipe(
                            tap(({ settings }) => {
                                const updatedSettings =
                                    JSON.parse(settings) as UserSettings;

                                patchState(store, {
                                    ...updatedSettings,
                                    settingsLoading: false,
                                });
                            }),
                            catchError((error) => {
                                patchState(store, {
                                    error,
                                    settingsLoading: false,
                                });

                                return EMPTY;
                            }),
                        );
                    }),
                ),
            ),

            deleteUserSettings: rxMethod<void>(
                pipe(
                    switchMap(() => {
                        return api.deleteCurrentUserSettings().pipe(

                            catchError((error) => {
                                patchState(store, {
                                    error,
                                });
                                return EMPTY;
                            })
                        );
                    }),
                    tap(() => {
                        patchState(store, {
                            ...initialState,
                        });
                    }),
                    tap(() => {
                        methods.inizializeSettingsStore();
                    })
                )

            ),

        }
        return methods;
    }),
    withHooks({
        onInit(store, auth = inject(AuthService)) {
            store.inizializeSettingsStore();

            effect(() => {
                const userSettings = auth.currentUser()?.settings;

                if (!userSettings) {
                    return;
                }
                untracked(() => {
                    if (userSettings) {
                        patchState(store, {
                            ...userSettings as UserSettings,
                        });
                    }
                });
            }),
                effect((onCleanup) => {
                    if (store.settingsLoading()) {
                        const timeout = setTimeout(() => {
                            patchState(store, {
                                debouncedSettingsLoading: true,
                            });
                        }, 250);

                        onCleanup(() => clearTimeout(timeout));
                    } else {
                        patchState(store, {
                            debouncedSettingsLoading: false,
                        });
                    }
                });
        },
    })
);
